import type { ApiProduct } from '../../../../../../models/ecommerce/product/ApiProduct';
import type { HybrisPromotion } from '../../../hybris/hybrisPromotionResult';
import type { PriceData } from '../../../priceData/priceData';
import type { ProductVariantProps } from '../../../props/itemTeaserProps';
import type {
    CampaignEcomPromotion,
    EdlpEcomPromotion,
    EcomPromotion,
    FixedEcomPromotion,
    MultiBuyEcomPromotion,
    PercentageEcomPromotion,
} from './promotion';

import { appInsights } from '../../../../../../appInsights';
import {
    KnownPromotionUsageUnitMap,
    type PromotionMaxUsage,
    promotionMaxUsageSchema,
} from './maxUsage';
import { CampaignPromoType, EDLP_PROMO_TYPE, isKnownCampaignPromoType } from './promotion';

export const isMultiBuyPromotion = (
    promo: EcomPromotion | undefined,
): promo is MultiBuyEcomPromotion => !!promo && promo.type === CampaignPromoType.MULTIBUY;

export const isFixedPricePromotion = (
    promo: EcomPromotion | undefined,
): promo is FixedEcomPromotion => !!promo && promo.type === CampaignPromoType.FIXED_PRICE;

export const isPercentagePromotion = (
    promo: EcomPromotion | undefined,
): promo is PercentageEcomPromotion => !!promo && promo.type === CampaignPromoType.PERCENTAGE;

export const isEdlpPromotion = (promo: EcomPromotion | undefined): promo is EdlpEcomPromotion =>
    !!promo && promo.type === EDLP_PROMO_TYPE;

/** Temporary campaign promo (not EDLP). */
export const isCampaignPromotion = (
    promo: EcomPromotion | undefined,
): promo is CampaignEcomPromotion => !!promo && isKnownCampaignPromoType(promo.type);

interface CartItemPriceMap {
    discount?: PriceData;
    piecePrice?: PriceData;
    totalPrice?: PriceData;
    totalPriceWithoutDiscount?: PriceData;
}

export interface OrderEntryPriceMap {
    discount?: PriceData;
    piecePrice: PriceData;
    totalPrice?: PriceData;
    totalPriceWithoutDiscount?: PriceData;
}

// Return type interfaces
interface OrderEntryPriceResult {
    piecePrice: PriceData;
    totalPrice?: PriceData;
    /** Think before using this once since its calculated by us */
    newPiecePrice?: PriceData;
    newTotalPrice?: PriceData;
}

interface CartItemPriceResult {
    piecePrice?: PriceData;
    totalPrice?: PriceData;
    /** Think before using this once since its calculated by us */
    newPiecePrice?: PriceData;
    newTotalPrice?: PriceData;
}

export function mapPrices(prices: OrderEntryPriceMap): OrderEntryPriceResult;
export function mapPrices(prices: CartItemPriceMap): CartItemPriceResult;
// eslint-disable-next-line func-style
export function mapPrices(
    prices: OrderEntryPriceMap | CartItemPriceMap,
): OrderEntryPriceResult | CartItemPriceResult {
    if (!prices.discount) {
        return {
            piecePrice: prices.piecePrice,
            totalPrice: prices.totalPrice,
        };
    }
    const factor =
        prices.totalPrice?.inclTaxPrice && prices.totalPriceWithoutDiscount?.inclTaxPrice
            ? prices.totalPrice.inclTaxPrice / prices.totalPriceWithoutDiscount.inclTaxPrice
            : 1;
    const newPiecePrice =
        prices.piecePrice?.inclTaxPrice && prices.piecePrice.exclTaxPrice
            ? {
                  inclTaxPrice: factor * prices.piecePrice.inclTaxPrice,
                  exclTaxPrice: factor * prices.piecePrice.exclTaxPrice,
              }
            : undefined;
    return {
        piecePrice: prices.piecePrice,
        totalPrice: prices.totalPriceWithoutDiscount,
        newPiecePrice,
        newTotalPrice: prices.totalPrice,
    };
}

export const resolveApiProductVariant = (
    product: ApiProduct,
    variantId?: string,
): ProductVariantProps | undefined => {
    if (!variantId) {
        return undefined;
    }

    const variant = product.variants?.find((item) => item.id === variantId);

    if (!variant) {
        return undefined;
    }

    return {
        id: variant.id,
        name: variant.name,
        priceData: variant.priceData,
        promotionPriceData: variant.promotionPriceData,
    };
};

export const mapApiProductVariantPriceData = (product: ApiProduct, variantId?: string) =>
    mapVariantPriceData(
        {
            priceData: product.priceData,
            promotionPriceData: product.promotionPriceData,
            promotion: product.promo,
        },
        resolveApiProductVariant(product, variantId),
    );

export const mapVariantPriceData = (
    product: {
        promotion: EcomPromotion | undefined;
        priceData: PriceData | undefined;
        promotionPriceData: PriceData | undefined;
    },
    currentVariant?: ProductVariantProps,
) => {
    const promotion = mapVariantPromotion(product.promotion, currentVariant);
    const promotionPriceData = currentVariant?.promotionPriceData || product.promotionPriceData;
    const priceData = currentVariant?.priceData || product.priceData;

    return {
        promotion,
        promotionPriceData,
        priceData,
    };
};

export const mapVariantPromotion = (
    promo?: EcomPromotion,
    currentVariant?: ProductVariantProps,
): EcomPromotion | undefined => {
    if (currentVariant && isFixedPricePromotion(promo)) {
        return {
            ...promo,
            fixedPiecePrice: currentVariant.promotionPriceData,
        };
    }
    return promo;
};

export const mapPromotionMaxUsage = (hPromo: HybrisPromotion): PromotionMaxUsage | undefined => {
    if (
        hPromo.maxUseText == null &&
        hPromo.maxUseValue == null &&
        hPromo.maxUseUnit == null &&
        hPromo.maxUsePerCustomer == null
    ) {
        return undefined; // not even one max usage info properties on promotion, dont even start parsing
    }

    const hybrisPromotionMaxUsageToDomainUnitMap: Record<string, PromotionMaxUsage['unit']> = {
        styck: KnownPromotionUsageUnitMap.piece,
        kg: KnownPromotionUsageUnitMap.kg,
    };
    const unit = hPromo.maxUseUnit ? String(hPromo.maxUseUnit).toLowerCase() : undefined;

    const { success, data, error } = promotionMaxUsageSchema.safeParse({
        value: hPromo.maxUseValue,
        unit:
            unit &&
            (hybrisPromotionMaxUsageToDomainUnitMap[unit] ||
                KnownPromotionUsageUnitMap.unknown(unit)),
        scope: hPromo.maxUsePerCustomer,
    });

    if (!success) {
        const exception = new Error('Failed to map Hybris promotion max usage into domain object');
        const properties = { hybrisPromotion: hPromo, parsingErrors: error };
        appInsights.trackException({
            severityLevel: 3,
            exception,
            properties,
        });
        return undefined;
    }

    if (data.unit.code === 'unknown') {
        const exception = new Error('Unknown Hybris promotion max usage unit encountered');
        const properties = { hybrisPromotion: hPromo, parsingErrors: error };
        appInsights.trackException({
            severityLevel: 2,
            exception,
            properties,
        });
        return data; // still return the data with unknown unit as-is
    }

    return data;
};
